// Mi Actividad — tablero personal del abogado: expedientes donde es Apoderado
// o Co-apoderado y que están sin movimientos desde una fecha de corte.
//
// Pensado para que el abogado detecte de un vistazo qué causas se le están
// quedando dormidas y pueda accionar (abrir el exp, dejar nota, generar
// movimiento, etc).

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Box, Paper, Typography, TextField, Button, IconButton, Tooltip, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TableSortLabel, Stack, Skeleton, Grid, Autocomplete, Checkbox,
    TablePagination, FormControlLabel, Switch, Collapse, Divider,
    Tabs, Tab, Menu, MenuItem, ListItemIcon, ListItemText,
    Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryIcon from '@mui/icons-material/History';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import TuneIcon from '@mui/icons-material/Tune';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LabelIcon from '@mui/icons-material/Label';
import LabelOffIcon from '@mui/icons-material/LabelOff';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import * as XLSX from 'xlsx';
import {
    inactividadListarJWT, inactividadStatsJWT, getCombosJWT,
    misTabsListarJWT, misTabsCrearJWT, misTabsActualizarJWT, misTabsEliminarJWT,
    misTabsAsignarJWT, misTabsDesasignarJWT, misTabsAsignarMasivoJWT,
} from '../../api/endpoints';
import SelectorListas from '../../components/ListasTrabajo/Compartidos/SelectorListas';

const PRIMARY = '#1769aa';
const BR = 4;

// Presets de "sin movimientos desde". Cada uno dispara setFecha con la fecha
// calculada (hoy - N días). El admin puede igual ingresar una fecha custom.
const PRESETS = [
    { key: 30, label: '30 días' },
    { key: 60, label: '60 días' },
    { key: 90, label: '90 días' },
    { key: 180, label: '180 días' },
];

function fechaMenosDias(dias) {
    const d = new Date();
    d.setDate(d.getDate() - dias);
    return d.toISOString().slice(0, 10);
}

function colorFila(dias) {
    if (dias > 180) return '#ffebee';   // rojo
    if (dias > 90)  return '#fff3e0';   // naranja
    if (dias > 60)  return '#fffde7';   // amarillo
    return 'transparent';
}

function fmtFecha(s) {
    if (!s) return '—';
    try { return new Date(s).toLocaleDateString('es-AR'); } catch { return s; }
}

function exportarExcel(rows) {
    const data = rows.map(r => ({
        Autos: r.Autos || '',
        Carátula: r.Caratula || '',
        Área: r.Area || '',
        'Tipo Proceso': r.Tipo_Proceso || '',
        Estado: r.Estado || '',
        'Mi rol': r.Mi_Caracter === 3 ? 'Apoderado' : r.Mi_Caracter === 8 ? 'Co-apoderado' : '',
        'Último mov fecha': fmtFecha(r.Ultimo_Mov_Fecha),
        'Último mov tipo': r.Ultimo_Mov_Tipo || '(Inicio del expediente)',
        'Días sin mov': r.Dias_Sin_Mov,
        'Próximo evento': r.Proximo_Evento_Fecha
            ? `${fmtFecha(r.Proximo_Evento_Fecha)} — ${r.Proximo_Evento_Tipo} — ${r.Proximo_Evento_Titulo}`
            : '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [{ wch: 12 }, { wch: 45 }, { wch: 22 }, { wch: 18 }, { wch: 16 }, { wch: 12 }, { wch: 14 }, { wch: 22 }, { wch: 12 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Mi Actividad');
    const hoy = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `mi-actividad-${hoy}.xlsx`);
}

export default function InactividadHome({ usuario }) {
    // Default vacío = sin filtro por días sin mov. El abogado activa el filtro
    // con los chips (30/60/90/180) o tipeando fecha exacta si lo necesita.
    const [fechaCorte, setFechaCorte] = useState('');
    // Filtro GLOBAL por fecha de asignación del expediente. Aplica a stats y al
    // listado para excluir expedientes viejos sin cerrar (la cola larga que
    // ensucia la vista). Default vacío = sin límite.
    const [fechaAsignacionDesde, setFechaAsignacionDesde] = useState('');
    const [busqueda, setBusqueda] = useState('');
    const [modulosSel, setModulosSel] = useState([]);
    const [tiposProcesoSel, setTiposProcesoSel] = useState([]);
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [orderBy, setOrderBy] = useState('Dias_Sin_Mov');
    const [orderDir, setOrderDir] = useState('desc');
    const [modulosOpts, setModulosOpts] = useState([]);
    const [tiposProcesoOpts, setTiposProcesoOpts] = useState([]);
    // Solo en trámite ON por default — los que no están en trámite ya no requieren acción.
    const [soloEnTramite, setSoloEnTramite] = useState(true);
    const [page, setPage] = useState(0);          // 0-based para MUI
    const [pageSize, setPageSize] = useState(50);
    // Selección de filas para enviar a una Lista de Trabajo.
    const [seleccionados, setSeleccionados] = useState(() => new Set());
    const [selectorListasOpen, setSelectorListasOpen] = useState(false);
    // Panel "Más filtros" (avanzados, replicados de ExpedientesHome).
    // Por default oculto para no saturar la UI; el abogado lo abre cuando lo necesita.
    const [masFiltros, setMasFiltros] = useState(false);
    // State único para filtros avanzados — mismo patrón que ExpedientesHome.
    const [fa, setFa] = useState({}); // filtrosAvanzados
    // Catálogos para los selects de los filtros avanzados.
    const [catEstados, setCatEstados] = useState([]);
    const [catJuzgados, setCatJuzgados] = useState([]);
    const [catEtapas, setCatEtapas] = useState([]);
    const [catObjetos, setCatObjetos] = useState([]);
    const [catReparticiones, setCatReparticiones] = useState([]);
    // ====== Tabs personales del abogado ======
    // Cada abogado puede crear sus propias "carpetas" para organizar sus exp.
    // tabActivo: null=Todos | número=Id del tab | 'sin-asignar' (sin tab).
    const [tabs, setTabs] = useState([]);
    // Counts del universo filtrado (respeta filtros globales). Se usa para mostrar
    // contadores en cada tab del header — coherentes con el listado.
    const [tabsCount, setTabsCount] = useState({ cantTotal: 0, cantSinAsignar: 0 });
    const [tabActivo, setTabActivo] = useState(null);
    // Menú "asignar a tab" por fila (anchorEl + idExp del expediente clickeado).
    const [menuTab, setMenuTab] = useState({ anchor: null, idExp: null });
    // Menú "asignar a tab" BULK — para los expedientes tildados con checkbox.
    const [menuTabBulk, setMenuTabBulk] = useState(null);
    // Dialog de gestión de tabs (crear/renombrar/eliminar/color).
    const [tabsDialogOpen, setTabsDialogOpen] = useState(false);

    // Cargar catálogos para los filtros (módulos y tipos de proceso del usuario).
    useEffect(() => {
        const opts = (usuario?.modulos || [])
            .filter(m => m.clave === 'expediente_judicial')
            .reduce((acc, m) => {
                if (!acc.some(x => x.id === m.id)) acc.push({ id: m.id, label: m.nombre });
                return acc;
            }, []);
        setModulosOpts(opts);

        getCombosJWT('tipos_proceso').then(r => {
            if (r?.ok) setTiposProcesoOpts((r.items || []).map(t => ({ id: t.id, label: t.label })));
        }).catch(() => { });
        // Catálogos para filtros avanzados — se cargan una sola vez al montar.
        // Errores silenciosos: si un combo no responde, el filtro queda con
        // dropdown vacío pero el panel sigue funcionando.
        getCombosJWT('estados_proceso').then(r => { if (r?.ok) setCatEstados((r.items || []).map(x => ({ id: x.id, label: x.label }))); }).catch(() => {});
        getCombosJWT('juzgados').then(r => { if (r?.ok) setCatJuzgados((r.items || []).map(x => ({ id: x.id, label: x.label }))); }).catch(() => {});
        getCombosJWT('etapas_proceso').then(r => { if (r?.ok) setCatEtapas((r.items || []).map(x => ({ id: x.id, label: x.label }))); }).catch(() => {});
        getCombosJWT('objetos_reclamo').then(r => { if (r?.ok) setCatObjetos((r.items || []).map(x => ({ id: x.id, label: x.label }))); }).catch(() => {});
        getCombosJWT('reparticiones').then(r => { if (r?.ok) setCatReparticiones((r.items || []).map(x => ({ id: x.id, label: x.label }))); }).catch(() => {});
    }, [usuario]);

    const cargarStats = useCallback(() => {
        inactividadStatsJWT({
            fechaAsignacionDesde: fechaAsignacionDesde || undefined,
            soloEnTramite,
        })
            .then(r => { if (r?.ok) setStats(r.stats); }).catch(() => { });
    }, [fechaAsignacionDesde, soloEnTramite]);

    // Carga las tabs personales del abogado. Respeta filtros globales para que
    // los contadores coincidan con el listado.
    const cargarTabs = useCallback(() => {
        misTabsListarJWT({
            fechaAsignacionDesde: fechaAsignacionDesde || undefined,
            soloEnTramite,
        })
            .then(r => {
                if (r?.ok) {
                    setTabs(r.tabs || []);
                    setTabsCount({
                        cantTotal: r.cantTotal || 0,
                        cantSinAsignar: r.cantSinAsignar || 0,
                    });
                }
            })
            .catch(() => { });
    }, [fechaAsignacionDesde, soloEnTramite]);
    useEffect(() => { cargarTabs(); }, [cargarTabs]);

    // Asigna o desasigna un expediente. Si idTab es null/0 → desasigna.
    const asignarTab = async (idExp, idTab) => {
        try {
            if (!idTab) {
                await misTabsDesasignarJWT(idExp);
            } else {
                await misTabsAsignarJWT(idTab, idExp);
            }
            // Refrescamos listado (para que la columna Tab cambie) y la cuenta
            // de exp por tab (badge en cada tab).
            await cargar();
            cargarTabs();
        } catch (e) {
            console.error('Error asignando tab:', e);
        } finally {
            setMenuTab({ anchor: null, idExp: null });
        }
    };

    // Bulk: asigna o desasigna todos los expedientes tildados con checkbox.
    // Si idTab es null → desasigna. Backend hace todo en una sola query.
    const asignarTabBulk = async (idTab) => {
        const ids = Array.from(seleccionados);
        if (ids.length === 0) return;
        try {
            await misTabsAsignarMasivoJWT(ids, idTab);
            setSeleccionados(new Set()); // limpiar selección post-acción
            await cargar();
            cargarTabs();
        } catch (e) {
            console.error('Error asignando bulk:', e);
        } finally {
            setMenuTabBulk(null);
        }
    };

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await inactividadListarJWT({
                fechaCorte,
                fechaAsignacionDesde: fechaAsignacionDesde || undefined,
                modulos: modulosSel.map(m => m.id),
                tiposProceso: tiposProcesoSel.map(t => t.id),
                busqueda,
                soloEnTramite,
                page: page + 1,
                pageSize,
                idTab: tabActivo,
                // Filtros avanzados
                autos: fa.autos || undefined,
                caratula: fa.caratula || undefined,
                certificado: fa.certificado || undefined,
                cartaPoder: fa.cartaPoder || undefined,
                demandado: fa.demandado || undefined,
                estadosProceso: (fa.estadosProceso || []).map(o => o.id),
                juzgado: (fa.juzgado || []).map(o => o.id),
                etapasProcesales: (fa.etapasProcesales || []).map(o => o.id),
                objetoReclamo: (fa.objetoReclamo || []).map(o => o.id),
                reparticionOrigen: (fa.reparticionOrigen || []).map(o => o.id),
                montoDesde: fa.montoDesde,
                montoHasta: fa.montoHasta,
                fechaInicioDesde: fa.fechaInicioDesde || undefined,
                fechaInicioHasta: fa.fechaInicioHasta || undefined,
                fechaAsignacionHasta: fa.fechaAsignacionHasta || undefined,
            });
            if (r?.ok) {
                setItems(r.items || []);
                setTotal(r.total || 0);
            }
        } finally { setLoading(false); }
    }, [fechaCorte, fechaAsignacionDesde, modulosSel, tiposProcesoSel, busqueda, soloEnTramite, page, pageSize, fa, tabActivo]);

    useEffect(() => { cargarStats(); }, [cargarStats]);
    useEffect(() => { cargar(); }, [cargar]);
    // Cuando cambia un filtro (no la página), volvemos a la primera página
    // y limpiamos la selección de filas (los IDs visibles pueden cambiar).
    useEffect(() => {
        setPage(0);
        setSeleccionados(new Set());
    }, [fechaCorte, fechaAsignacionDesde, modulosSel, tiposProcesoSel, busqueda, soloEnTramite, fa, tabActivo]);

    // Toggle de selección por fila (para enviar a lista)
    const toggleSeleccion = (id) => {
        setSeleccionados(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    // Toggle de "seleccionar todos los visibles"
    const toggleSeleccionarTodos = (visibles) => {
        setSeleccionados(prev => {
            const todasSeleccionadas = visibles.every(r => prev.has(r.Id));
            const next = new Set(prev);
            if (todasSeleccionadas) {
                visibles.forEach(r => next.delete(r.Id));
            } else {
                visibles.forEach(r => next.add(r.Id));
            }
            return next;
        });
    };

    // Cantidad de filtros avanzados activos — sirve para mostrar badge en el
    // botón "Más filtros (N)" y avisar al usuario que tiene filtros aplicados.
    const masFiltrosCount = useMemo(() => {
        let n = 0;
        if (fa.autos) n++;
        if (fa.caratula) n++;
        if (fa.certificado) n++;
        if (fa.cartaPoder) n++;
        if (fa.demandado) n++;
        if (fa.estadosProceso && fa.estadosProceso.length) n++;
        if (fa.juzgado && fa.juzgado.length) n++;
        if (fa.etapasProcesales && fa.etapasProcesales.length) n++;
        if (fa.objetoReclamo && fa.objetoReclamo.length) n++;
        if (fa.reparticionOrigen && fa.reparticionOrigen.length) n++;
        if (fa.montoDesde != null && fa.montoDesde !== '') n++;
        if (fa.montoHasta != null && fa.montoHasta !== '') n++;
        if (fa.fechaInicioDesde) n++;
        if (fa.fechaInicioHasta) n++;
        if (fa.fechaAsignacionHasta) n++;
        // Área y Tipo Proceso ahora también viven en el panel "Más filtros"
        // (se sacaron de la barra principal para limpiar la UI).
        if (modulosSel && modulosSel.length) n++;
        if (tiposProcesoSel && tiposProcesoSel.length) n++;
        return n;
    }, [fa, modulosSel, tiposProcesoSel]);

    const sorted = useMemo(() => {
        const sign = orderDir === 'asc' ? 1 : -1;
        const arr = [...items];
        arr.sort((a, b) => {
            const va = a[orderBy] ?? '';
            const vb = b[orderBy] ?? '';
            if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sign;
            return String(va).localeCompare(String(vb), 'es') * sign;
        });
        return arr;
    }, [items, orderBy, orderDir]);

    const handleSort = (col) => {
        if (orderBy === col) setOrderDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setOrderBy(col); setOrderDir('desc'); }
    };

    const abrirExp = (id) => {
        window.open(`/expediente/judicial/${id}`, '_blank', 'noopener,noreferrer');
    };

    const columnas = [
        { key: 'Autos',             label: 'Autos',          width: 110 },
        { key: 'Caratula',          label: 'Carátula' },
        { key: 'Area',              label: 'Área',           width: 160 },
        { key: 'Tipo_Proceso',      label: 'Tipo Proceso',   width: 140 },
        { key: 'Estado',            label: 'Estado',         width: 130 },
        { key: 'Ultimo_Mov_Fecha',  label: 'Último mov',     width: 110 },
        { key: 'Ultimo_Mov_Tipo',   label: 'Tipo último mov', width: 160 },
        { key: 'Dias_Sin_Mov',      label: 'Días sin mov',   width: 110, align: 'right' },
        { key: 'Proximo_Evento_Fecha', label: 'Próximo evento', width: 180 },
    ];

    return (
        <Box sx={{ p: 2.5, bgcolor: '#f6f8fc', minHeight: '100vh' }}>
            {/* Header */}
            <Paper sx={{
                p: 2.5, borderRadius: `${BR}px`, mb: 2,
                background: `linear-gradient(135deg, ${PRIMARY} 0%, #2196f3 100%)`, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
            }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Mi Actividad</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Expedientes donde sos apoderado o co-apoderado y están sin movimientos
                    </Typography>
                </Box>
                {/* Filtros GLOBALES — aplican a las tarjetas de stats Y al listado.
                    "Asignados desde" excluye expedientes viejos.
                    "Solo en trámite" excluye expedientes archivados / concluidos. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: `${BR}px`, p: 1, pl: 1.5 }}>
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        Asignados desde:
                    </Typography>
                    <TextField
                        type="date" size="small"
                        InputLabelProps={{ shrink: true }}
                        value={fechaAsignacionDesde}
                        onChange={e => setFechaAsignacionDesde(e.target.value)}
                        sx={{
                            width: 160, bgcolor: 'white', borderRadius: `${BR}px`,
                            '& .MuiOutlinedInput-root': { fontSize: '0.85rem' },
                        }}
                    />
                    {fechaAsignacionDesde && (
                        <Tooltip title="Quitar filtro de asignación">
                            <IconButton size="small" onClick={() => setFechaAsignacionDesde('')}
                                sx={{ color: 'white' }}>
                                <span style={{ fontSize: 18, fontWeight: 700 }}>×</span>
                            </IconButton>
                        </Tooltip>
                    )}
                    <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.4)', mx: 0.5 }} />
                    <FormControlLabel
                        control={<Switch checked={soloEnTramite} onChange={e => setSoloEnTramite(e.target.checked)} size="small"
                            sx={{ '& .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.4)' } }} />}
                        label={<Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>Solo en trámite</Typography>}
                        sx={{ ml: 0, mr: 0 }}
                    />
                </Box>
                <Tooltip title="Refrescar"><IconButton onClick={() => { cargarStats(); cargar(); }}
                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                    <RefreshIcon />
                </IconButton></Tooltip>
            </Paper>

            {/* Stats — cards compactos para destacar el contenido de abajo. */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid size={{ xs: 6, md: 3 }}>
                    <Paper sx={{ px: 1.5, py: 1, borderRadius: `${BR}px`, borderLeft: `3px solid ${PRIMARY}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', lineHeight: 1.2, flex: 1 }}>
                            Total como<br />apoderado
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, fontSize: '1.4rem' }}>{stats?.total ?? '—'}</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <Paper sx={{ px: 1.5, py: 1, borderRadius: `${BR}px`, borderLeft: '3px solid #fbc02d', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', lineHeight: 1.2, flex: 1 }}>
                            +30 días<br />sin mov
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#f57f17', fontSize: '1.4rem' }}>{stats?.mas_30_dias ?? '—'}</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <Paper sx={{ px: 1.5, py: 1, borderRadius: `${BR}px`, borderLeft: '3px solid #f57c00', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', lineHeight: 1.2, flex: 1 }}>
                            <WarningAmberIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                            +90 días<br />sin mov
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#e65100', fontSize: '1.4rem' }}>{stats?.mas_90_dias ?? '—'}</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <Paper sx={{ px: 1.5, py: 1, borderRadius: `${BR}px`, borderLeft: '3px solid #c62828', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', lineHeight: 1.2, flex: 1 }}>
                            <ErrorOutlineIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                            +180 días<br />sin mov
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#b71c1c', fontSize: '1.4rem' }}>{stats?.mas_180_dias ?? '—'}</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Filtros */}
            <Paper sx={{ p: 1.8, borderRadius: `${BR}px`, mb: 2 }}>
                <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>Días sin mov:</Typography>
                    {/* Chip "Todos" = sin filtro de inactividad (default). */}
                    <Chip
                        label="Todos"
                        size="small"
                        color={!fechaCorte ? 'primary' : 'default'}
                        onClick={() => setFechaCorte('')}
                        sx={{ cursor: 'pointer', fontWeight: 600 }} />
                    {PRESETS.map(p => (
                        <Chip key={p.key}
                            label={p.label}
                            size="small"
                            color={fechaCorte === fechaMenosDias(p.key) ? 'primary' : 'default'}
                            onClick={() => setFechaCorte(fechaMenosDias(p.key))}
                            sx={{ cursor: 'pointer', fontWeight: 600 }} />
                    ))}
                    <TextField
                        type="date" size="small" label="último mov hasta"
                        InputLabelProps={{ shrink: true }}
                        value={fechaCorte} onChange={e => setFechaCorte(e.target.value)}
                        sx={{ width: 180 }}
                    />
                    <TextField
                        size="small" label="Buscar autos/carátula"
                        value={busqueda} onChange={e => setBusqueda(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') cargar(); }}
                        sx={{ width: 230 }}
                        InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ color: '#888', mr: 0.5 }} /> }}
                    />
                    {/* "Más filtros" queda con los demás filtros, NO en el grupo de acciones.
                        "Solo en trámite" se movió arriba al header (filtro GLOBAL, aplica a cards). */}
                    <Button
                        startIcon={<TuneIcon />}
                        endIcon={masFiltros ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        variant={masFiltrosCount > 0 ? 'contained' : 'outlined'}
                        color={masFiltrosCount > 0 ? 'primary' : 'inherit'}
                        size="small"
                        onClick={() => setMasFiltros(m => !m)}
                        sx={{ textTransform: 'none', borderRadius: `${BR}px` }}>
                        Más filtros {masFiltrosCount > 0 && `(${masFiltrosCount})`}
                    </Button>
                    {/* Excel se queda solo en la fila principal de filtros, a la derecha. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, ml: 'auto', flexWrap: 'wrap' }}>
                        <Button startIcon={<FileDownloadIcon />} variant="outlined" size="small"
                            onClick={() => exportarExcel(sorted)}
                            disabled={sorted.length === 0}
                            sx={{ textTransform: 'none', borderRadius: `${BR}px` }}>
                            Excel (página actual)
                        </Button>
                    </Box>
                </Stack>

                {/* Acciones BULK — aparecen en una segunda fila debajo a la derecha
                    solo cuando hay filas tildadas. Así no interfieren con la fila de
                    filtros y el Excel se mantiene en su lugar. */}
                {seleccionados.size > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.2, mt: 1.5, flexWrap: 'wrap' }}>
                        <Button startIcon={<LabelIcon />} variant="contained" size="small" color="primary"
                            onClick={e => setMenuTabBulk(e.currentTarget)}
                            sx={{ textTransform: 'none', borderRadius: `${BR}px` }}>
                            Asignar {seleccionados.size} a tab
                        </Button>
                        <Button startIcon={<PlaylistAddIcon />} variant="contained" size="small" color="success"
                            onClick={() => setSelectorListasOpen(true)}
                            sx={{ textTransform: 'none', borderRadius: `${BR}px` }}>
                            Agregar {seleccionados.size} a lista
                        </Button>
                    </Box>
                )}

                {/* Panel "Más filtros" — replica los filtros de ExpedientesHome
                    pero aplicados al subset de "mis expedientes". */}
                <Collapse in={masFiltros} timeout="auto">
                    <Divider sx={{ my: 1.5 }} />
                    <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField fullWidth size="small" label="Autos"
                                value={fa.autos || ''}
                                onChange={e => setFa(p => ({ ...p, autos: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField fullWidth size="small" label="Carátula"
                                value={fa.caratula || ''}
                                onChange={e => setFa(p => ({ ...p, caratula: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField fullWidth size="small" label="Certificado"
                                value={fa.certificado || ''}
                                onChange={e => setFa(p => ({ ...p, certificado: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField fullWidth size="small" label="Carta Poder"
                                value={fa.cartaPoder || ''}
                                onChange={e => setFa(p => ({ ...p, cartaPoder: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField fullWidth size="small" label="Demandado"
                                value={fa.demandado || ''}
                                onChange={e => setFa(p => ({ ...p, demandado: e.target.value }))} />
                        </Grid>
                        {/* Área (Módulo) y Tipo Proceso — se cargaron arriba (no en `fa`).
                            Los moví acá para que la barra principal quede más limpia. */}
                        {modulosOpts.length > 1 && (
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Autocomplete multiple size="small"
                                    options={modulosOpts}
                                    getOptionLabel={o => o.label || ''}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    value={modulosSel}
                                    onChange={(_, v) => setModulosSel(v)}
                                    disableCloseOnSelect
                                    renderInput={params => <TextField {...params} label="Área" placeholder="Todas" />}
                                    limitTags={1}
                                />
                            </Grid>
                        )}
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Autocomplete multiple size="small"
                                options={tiposProcesoOpts}
                                getOptionLabel={o => o.label || ''}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={tiposProcesoSel}
                                onChange={(_, v) => setTiposProcesoSel(v)}
                                disableCloseOnSelect
                                renderInput={params => <TextField {...params} label="Tipo Proceso" placeholder="Todos" />}
                                limitTags={1}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Autocomplete multiple size="small"
                                options={catEstados}
                                getOptionLabel={o => o.label || ''}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={fa.estadosProceso || []}
                                onChange={(_, v) => setFa(p => ({ ...p, estadosProceso: v }))}
                                disableCloseOnSelect
                                renderInput={params => <TextField {...params} label="Estado proceso" placeholder="Todos" />}
                                limitTags={1}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Autocomplete multiple size="small"
                                options={catJuzgados}
                                getOptionLabel={o => o.label || ''}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={fa.juzgado || []}
                                onChange={(_, v) => setFa(p => ({ ...p, juzgado: v }))}
                                disableCloseOnSelect
                                renderInput={params => <TextField {...params} label="Juzgado" placeholder="Todos" />}
                                limitTags={1}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Autocomplete multiple size="small"
                                options={catEtapas}
                                getOptionLabel={o => o.label || ''}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={fa.etapasProcesales || []}
                                onChange={(_, v) => setFa(p => ({ ...p, etapasProcesales: v }))}
                                disableCloseOnSelect
                                renderInput={params => <TextField {...params} label="Etapa procesal" placeholder="Todas" />}
                                limitTags={1}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Autocomplete multiple size="small"
                                options={catObjetos}
                                getOptionLabel={o => o.label || ''}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={fa.objetoReclamo || []}
                                onChange={(_, v) => setFa(p => ({ ...p, objetoReclamo: v }))}
                                disableCloseOnSelect
                                renderInput={params => <TextField {...params} label="Objeto reclamo" placeholder="Todos" />}
                                limitTags={1}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Autocomplete multiple size="small"
                                options={catReparticiones}
                                getOptionLabel={o => o.label || ''}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={fa.reparticionOrigen || []}
                                onChange={(_, v) => setFa(p => ({ ...p, reparticionOrigen: v }))}
                                disableCloseOnSelect
                                renderInput={params => <TextField {...params} label="Repartición origen" placeholder="Todas" />}
                                limitTags={1}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
                            <TextField fullWidth size="small" type="number" label="Monto desde"
                                value={fa.montoDesde ?? ''}
                                onChange={e => setFa(p => ({ ...p, montoDesde: e.target.value === '' ? undefined : e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
                            <TextField fullWidth size="small" type="number" label="Monto hasta"
                                value={fa.montoHasta ?? ''}
                                onChange={e => setFa(p => ({ ...p, montoHasta: e.target.value === '' ? undefined : e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
                            <TextField fullWidth size="small" type="date" label="F. inicio desde"
                                InputLabelProps={{ shrink: true }}
                                value={fa.fechaInicioDesde || ''}
                                onChange={e => setFa(p => ({ ...p, fechaInicioDesde: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
                            <TextField fullWidth size="small" type="date" label="F. inicio hasta"
                                InputLabelProps={{ shrink: true }}
                                value={fa.fechaInicioHasta || ''}
                                onChange={e => setFa(p => ({ ...p, fechaInicioHasta: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
                            <TextField fullWidth size="small" type="date" label="F. asign. hasta"
                                InputLabelProps={{ shrink: true }}
                                value={fa.fechaAsignacionHasta || ''}
                                onChange={e => setFa(p => ({ ...p, fechaAsignacionHasta: e.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                            <Button size="small" startIcon={<ClearAllIcon />}
                                onClick={() => { setFa({}); setModulosSel([]); setTiposProcesoSel([]); }}
                                disabled={masFiltrosCount === 0}
                                sx={{ textTransform: 'none' }}>
                                Limpiar filtros avanzados
                            </Button>
                        </Grid>
                    </Grid>
                </Collapse>
            </Paper>

            {/* Tabs personales — organización propia del abogado (no afecta proceso).
                Si no tiene ningún tab, mostramos CTA prominente para crear el primero.
                Si tiene tabs: barra con "Todos | tab1 | tab2 | ... | Sin asignar | + Editar" */}
            <Paper sx={{ borderRadius: `${BR}px`, mb: 2, display: 'flex', alignItems: 'center', minHeight: 48 }}>
                {tabs.length === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', px: 2, py: 1, gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666' }}>
                            <LabelIcon fontSize="small" sx={{ color: '#999' }} />
                            <Typography variant="body2">
                                <b>Tabs personales:</b> creá etiquetas privadas para organizar tus expedientes
                                (ej: <em>"Revisar"</em>, <em>"Audiencia"</em>, <em>"Apelado"</em>).
                            </Typography>
                        </Box>
                        <Button variant="contained" size="small" startIcon={<AddIcon />}
                            onClick={() => setTabsDialogOpen(true)}
                            sx={{ textTransform: 'none', borderRadius: `${BR}px`, whiteSpace: 'nowrap' }}>
                            Crear mi primer tab
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Tabs
                            value={tabActivo === null ? 'todos' : (tabActivo === 'sin-asignar' ? 'sin-asignar' : tabActivo)}
                            onChange={(_, v) => setTabActivo(v === 'todos' ? null : v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ flex: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 } }}
                        >
                            <Tab value="todos" label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                    <span>Todos</span>
                                    <Chip label={tabsCount.cantTotal} size="small"
                                        sx={{ height: 18, fontSize: '0.7rem', ml: 0.3 }} />
                                </Box>
                            } />
                            {tabs.map(t => (
                                <Tab key={t.Id} value={t.Id}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                            {t.Color && <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: t.Color }} />}
                                            <span>{t.Nombre}</span>
                                            <Chip label={t.Cant_Expedientes || 0} size="small"
                                                sx={{ height: 18, fontSize: '0.7rem', ml: 0.3 }} />
                                        </Box>
                                    }
                                />
                            ))}
                            <Tab value="sin-asignar" label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                    <em style={{ color: '#888' }}>Sin asignar</em>
                                    <Chip label={tabsCount.cantSinAsignar} size="small"
                                        sx={{ height: 18, fontSize: '0.7rem', ml: 0.3 }} />
                                </Box>
                            } />
                        </Tabs>
                        <Tooltip title="Crear / editar / eliminar tabs">
                            <Button size="small" startIcon={<EditIcon />}
                                onClick={() => setTabsDialogOpen(true)}
                                sx={{ mx: 1, textTransform: 'none', whiteSpace: 'nowrap' }}>
                                Mis tabs
                            </Button>
                        </Tooltip>
                    </>
                )}
            </Paper>

            {/* Tabla */}
            <TableContainer component={Paper} sx={{ borderRadius: `${BR}px` }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ width: 42 }}>
                                <Checkbox size="small"
                                    indeterminate={seleccionados.size > 0 && sorted.some(r => !seleccionados.has(r.Id))}
                                    checked={sorted.length > 0 && sorted.every(r => seleccionados.has(r.Id))}
                                    onChange={() => toggleSeleccionarTodos(sorted)}
                                />
                            </TableCell>
                            {columnas.map(c => (
                                <TableCell key={c.key} align={c.align || 'left'} sx={{ fontWeight: 700, width: c.width }}>
                                    <TableSortLabel
                                        active={orderBy === c.key}
                                        direction={orderBy === c.key ? orderDir : 'asc'}
                                        onClick={() => handleSort(c.key)}>
                                        {c.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                            <TableCell sx={{ fontWeight: 700, width: 140 }} align="center">Tab</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: 60 }} align="center">Abrir</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={columnas.length + 3}><Skeleton variant="rectangular" height={200} /></TableCell></TableRow>
                        ) : sorted.length === 0 ? (
                            <TableRow><TableCell colSpan={columnas.length + 3} sx={{ textAlign: 'center', py: 5, color: '#888' }}>
                                <HistoryIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                                <Typography>No tenés expedientes inactivos en este rango.</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Probá cambiar la fecha de corte o los filtros.
                                </Typography>
                            </TableCell></TableRow>
                        ) : sorted.map(r => (
                            <TableRow key={r.Id} hover sx={{ bgcolor: colorFila(r.Dias_Sin_Mov), cursor: 'pointer' }}
                                onClick={() => abrirExp(r.Id)}>
                                <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                                    <Checkbox size="small"
                                        checked={seleccionados.has(r.Id)}
                                        onChange={() => toggleSeleccion(r.Id)}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.Autos || '—'}</TableCell>
                                <TableCell sx={{ fontSize: '0.85rem' }}>{r.Caratula}</TableCell>
                                <TableCell sx={{ fontSize: '0.82rem', color: '#555' }}>{r.Area || '—'}</TableCell>
                                <TableCell sx={{ fontSize: '0.82rem', color: '#555' }}>{r.Tipo_Proceso || '—'}</TableCell>
                                <TableCell sx={{ fontSize: '0.82rem' }}>
                                    {r.Estado && <Chip size="small" label={r.Estado} sx={{ height: 20, fontSize: '0.7rem' }} />}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.82rem' }}>{fmtFecha(r.Ultimo_Mov_Fecha)}</TableCell>
                                <TableCell sx={{ fontSize: '0.78rem', color: '#666' }}>{r.Ultimo_Mov_Tipo || <em>(Inicio del exp)</em>}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: r.Dias_Sin_Mov > 180 ? '#b71c1c' : r.Dias_Sin_Mov > 90 ? '#e65100' : '#444' }}>
                                    {r.Dias_Sin_Mov}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.78rem' }}>
                                    {r.Proximo_Evento_Fecha ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#1565c0' }}>
                                                {fmtFecha(r.Proximo_Evento_Fecha)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>
                                                {r.Proximo_Evento_Tipo} · {r.Proximo_Evento_Titulo}
                                            </Typography>
                                        </Box>
                                    ) : <span style={{ color: '#ccc' }}>—</span>}
                                </TableCell>
                                <TableCell align="center" onClick={e => e.stopPropagation()}>
                                    {r.Id_Tab ? (
                                        <Tooltip title="Click para cambiar tab">
                                            <Chip
                                                size="small"
                                                label={r.Nombre_Tab}
                                                onClick={e => setMenuTab({ anchor: e.currentTarget, idExp: r.Id })}
                                                sx={{
                                                    bgcolor: r.Color_Tab || '#1976d2',
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    maxWidth: 130,
                                                }}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Asignar a un tab">
                                            <IconButton size="small"
                                                onClick={e => setMenuTab({ anchor: e.currentTarget, idExp: r.Id })}
                                                sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
                                                <LabelIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell align="center" onClick={e => e.stopPropagation()}>
                                    <IconButton size="small" onClick={() => abrirExp(r.Id)}>
                                        <OpenInNewIcon sx={{ fontSize: 16, color: PRIMARY }} />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {!loading && total > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #eee', pl: 1.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" color="text.secondary">Color de fila:</Typography>
                            <Chip label=">60 d" size="small" sx={{ bgcolor: '#fffde7', fontSize: '0.7rem', height: 18 }} />
                            <Chip label=">90 d" size="small" sx={{ bgcolor: '#fff3e0', fontSize: '0.7rem', height: 18 }} />
                            <Chip label=">180 d" size="small" sx={{ bgcolor: '#ffebee', fontSize: '0.7rem', height: 18 }} />
                        </Stack>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            rowsPerPage={pageSize}
                            onPageChange={(_, p) => setPage(p)}
                            onRowsPerPageChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
                            rowsPerPageOptions={[25, 50, 100, 200]}
                            labelRowsPerPage="Por página:"
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                        />
                    </Box>
                )}
            </TableContainer>

            {/* Selector de listas: se abre con el botón "Agregar N a lista" del toolbar.
                Pasamos los IDs seleccionados — todos son judiciales (Mi Actividad solo
                muestra exp donde el usuario es Apoderado / Co-apoderado). */}
            <SelectorListas
                open={selectorListasOpen}
                onClose={() => setSelectorListasOpen(false)}
                expedientesIds={Array.from(seleccionados)}
                tipoExpediente={1}
                onSuccess={() => {
                    setSelectorListasOpen(false);
                    setSeleccionados(new Set());
                }}
            />

            {/* Menú BULK "Asignar N a tab" — se abre desde el botón verde cuando hay
                filas tildadas. Misma estructura que el menú por fila pero llama a
                asignarTabBulk con todos los IDs seleccionados de una. */}
            <Menu
                anchorEl={menuTabBulk}
                open={!!menuTabBulk}
                onClose={() => setMenuTabBulk(null)}
            >
                {/* MUI Menu no acepta Fragment como hijo — devolvemos expresiones
                    inline en lugar de envolverlas en <>...</>. */}
                {tabs.length === 0 && (
                    <MenuItem onClick={() => { setMenuTabBulk(null); setTabsDialogOpen(true); }}>
                        <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Crear tu primer tab</ListItemText>
                    </MenuItem>
                )}
                {tabs.length > 0 && tabs.map(t => (
                    <MenuItem key={t.Id} onClick={() => asignarTabBulk(t.Id)}>
                        <ListItemIcon>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: t.Color || '#ddd', ml: 0.5 }} />
                        </ListItemIcon>
                        <ListItemText>{t.Nombre}</ListItemText>
                    </MenuItem>
                ))}
                {tabs.length > 0 && <Divider key="div1" />}
                {tabs.length > 0 && (
                    <MenuItem key="quitar" onClick={() => asignarTabBulk(null)}>
                        <ListItemIcon><LabelOffIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Quitar de tab (los {seleccionados.size})</ListItemText>
                    </MenuItem>
                )}
                {tabs.length > 0 && <Divider key="div2" />}
                {tabs.length > 0 && (
                    <MenuItem key="editar" onClick={() => { setMenuTabBulk(null); setTabsDialogOpen(true); }}>
                        <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Editar mis tabs...</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {/* Menú "Asignar a tab" — se abre desde el chip/icono en la columna Tab
                de cada fila. Lista las tabs del abogado + opción de desasignar. */}
            <Menu
                anchorEl={menuTab.anchor}
                open={!!menuTab.anchor}
                onClose={() => setMenuTab({ anchor: null, idExp: null })}
            >
                {tabs.length === 0 && (
                    <MenuItem disabled>
                        <em>No tenés tabs creados</em>
                    </MenuItem>
                )}
                {tabs.map(t => {
                    // Verificamos si el exp ya está en este tab para mostrar check.
                    const exp = items.find(x => x.Id === menuTab.idExp);
                    const yaEn = exp?.Id_Tab === t.Id;
                    return (
                        <MenuItem key={t.Id} onClick={() => asignarTab(menuTab.idExp, t.Id)}>
                            <ListItemIcon>
                                {yaEn
                                    ? <CheckIcon fontSize="small" sx={{ color: t.Color || '#1976d2' }} />
                                    : <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: t.Color || '#ddd', ml: 0.5 }} />}
                            </ListItemIcon>
                            <ListItemText>{t.Nombre}</ListItemText>
                        </MenuItem>
                    );
                })}
                <Divider />
                <MenuItem onClick={() => asignarTab(menuTab.idExp, null)}>
                    <ListItemIcon><LabelOffIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Quitar de tab</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { setMenuTab({ anchor: null, idExp: null }); setTabsDialogOpen(true); }}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Editar mis tabs...</ListItemText>
                </MenuItem>
            </Menu>

            {/* Dialog de edición de tabs personales (crear / renombrar / eliminar / color) */}
            <EditarTabsDialog
                open={tabsDialogOpen}
                onClose={() => setTabsDialogOpen(false)}
                tabs={tabs}
                onChange={() => { cargarTabs(); cargar(); }}
            />
        </Box>
    );
}

// ============================================================================
// Dialog de gestión de tabs personales. CRUD inline: ver lista, agregar al pie,
// renombrar / cambiar color / eliminar cada tab. Cambios persisten en backend.
// ============================================================================
function EditarTabsDialog({ open, onClose, tabs, onChange }) {
    const [nuevoNombre, setNuevoNombre] = useState('');
    const [nuevoColor, setNuevoColor] = useState('#1976d2');
    const [creando, setCreando] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editNombre, setEditNombre] = useState('');
    const [editColor, setEditColor] = useState('');
    const [confirmEliminar, setConfirmEliminar] = useState(null);

    const handleCrear = async () => {
        const nombre = nuevoNombre.trim();
        if (!nombre) return;
        setCreando(true);
        try {
            const r = await misTabsCrearJWT(nombre, nuevoColor || null);
            if (r?.ok) {
                setNuevoNombre('');
                setNuevoColor('#1976d2');
                onChange?.();
            }
        } finally { setCreando(false); }
    };

    const handleGuardarEdit = async (id) => {
        const nombre = editNombre.trim();
        if (!nombre) return;
        const r = await misTabsActualizarJWT(id, { nombre, color: editColor || null });
        if (r?.ok) { setEditId(null); onChange?.(); }
    };

    const handleEliminar = async (id) => {
        const r = await misTabsEliminarJWT(id);
        if (r?.ok) { setConfirmEliminar(null); onChange?.(); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: '4px' } }}>
            <DialogTitle sx={{ fontWeight: 700, color: '#1769aa' }}>
                Mis tabs personales
                <Typography variant="caption" sx={{ display: 'block', color: '#777', fontWeight: 400 }}>
                    Organizá tus expedientes con etiquetas propias. Solo vos las ves.
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <List dense>
                    {tabs.length === 0 && (
                        <Typography color="text.secondary" sx={{ p: 2, fontStyle: 'italic', textAlign: 'center' }}>
                            Todavía no tenés tabs. Agregá uno abajo.
                        </Typography>
                    )}
                    {tabs.map(t => editId === t.Id ? (
                        <ListItem key={t.Id} sx={{ display: 'flex', gap: 1 }}>
                            <input type="color" value={editColor || '#1976d2'} onChange={e => setEditColor(e.target.value)}
                                style={{ width: 36, height: 32, border: 'none', cursor: 'pointer' }} />
                            <TextField size="small" value={editNombre} onChange={e => setEditNombre(e.target.value)}
                                autoFocus fullWidth
                                onKeyDown={e => { if (e.key === 'Enter') handleGuardarEdit(t.Id); }}
                            />
                            <Button size="small" variant="contained" onClick={() => handleGuardarEdit(t.Id)}>
                                Guardar
                            </Button>
                            <Button size="small" onClick={() => setEditId(null)}>Cancelar</Button>
                        </ListItem>
                    ) : (
                        <ListItem key={t.Id}
                            secondaryAction={
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="Renombrar / cambiar color">
                                        <IconButton size="small" onClick={() => {
                                            setEditId(t.Id); setEditNombre(t.Nombre); setEditColor(t.Color || '#1976d2');
                                        }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar tab (los expedientes vuelven a Sin asignar)">
                                        <IconButton size="small" color="error" onClick={() => setConfirmEliminar(t.Id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            }
                        >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: t.Color || '#ddd' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={t.Nombre}
                                secondary={`${t.Cant_Expedientes || 0} expedientes asignados`}
                            />
                        </ListItem>
                    ))}
                </List>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                    Crear nuevo tab
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <input type="color" value={nuevoColor} onChange={e => setNuevoColor(e.target.value)}
                        style={{ width: 36, height: 40, border: 'none', cursor: 'pointer' }} />
                    <TextField size="small" placeholder="Nombre del tab" value={nuevoNombre}
                        onChange={e => setNuevoNombre(e.target.value)} fullWidth
                        onKeyDown={e => { if (e.key === 'Enter') handleCrear(); }}
                    />
                    <Button variant="contained" startIcon={<AddIcon />}
                        onClick={handleCrear} disabled={creando || !nuevoNombre.trim()}
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                        Crear
                    </Button>
                </Box>

                {/* Confirmación de eliminación — Dialog MUI estilizado, no window.confirm */}
                <Dialog open={confirmEliminar !== null} onClose={() => setConfirmEliminar(null)}>
                    <DialogTitle>¿Eliminar este tab?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Los expedientes asignados a este tab volverán a quedar como "Sin asignar".
                            Esta acción no se puede deshacer.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmEliminar(null)}>Cancelar</Button>
                        <Button color="error" variant="contained" onClick={() => handleEliminar(confirmEliminar)}>
                            Eliminar
                        </Button>
                    </DialogActions>
                </Dialog>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
}
